import { type Scope, createContextScope } from '@radix-ui/react-context'
import React, { useState } from 'react'
import { Content } from './components/content'
import { Footer } from './components/footer'
import { TimerBar } from './components/timer-bar'
import * as Comp from './snackbar.styles'
import type { SnackbarProps } from './snackbar.types'

type SnackbarContext = Omit<SnackbarProps, 'children' | 'testID'> & {
  hasFooter: boolean
  percentage: number
  setPercentage: (percentage: number) => void
}

const DISPLAY_NAME: string = 'Snackbar'
const FIVE_SECONDS_IN_MS: number = 5000

function Root(props: SnackbarProps) {
  const {
    full,
    children,
    type = 'brand-accent',
    duration = FIVE_SECONDS_IN_MS,
    layout,
    __scopeSnackbar,
    testID,
    withIcon = false,
    buttonLabel,
    onPressFooterButton,
  } = props as ScopedProps<SnackbarProps>

  let hasFooter = false

  const [percentage, setPercentage] = useState(0)

  const renderChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      const isValidElementToRender =
        child.type === TimerBar ||
        child.type === Content ||
        child.type === Footer

      if (child.type === Footer) {
        hasFooter = true
      }

      if (isValidElementToRender) {
        return child
      }

      throw new Error('O componente filho não é válido para o Snackbar')
    }
  })

  return (
    <SnackbarContextProvider
      scope={__scopeSnackbar}
      duration={duration}
      layout={layout}
      type={type}
      hasFooter={hasFooter}
      full={full}
      percentage={percentage}
      setPercentage={setPercentage}
      withIcon={withIcon}
      buttonLabel={buttonLabel}
      onPressFooterButton={onPressFooterButton}
    >
      <Comp.Root testID={testID} full={full} type={type}>
        {renderChildren}
      </Comp.Root>
    </SnackbarContextProvider>
  )
}

Root.displayName = DISPLAY_NAME

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeSnackbar?: Scope
}

const [createSnackbarContext] = createContextScope(DISPLAY_NAME)

export const [SnackbarContextProvider, useSnackbarContext]: readonly [
  React.FC<
    SnackbarContext & {
      scope: Scope<SnackbarContext>
      children: React.ReactNode
    }
  >,
  (consumerName: string, scope: Scope) => SnackbarContext,
] = createSnackbarContext<SnackbarContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const Snackbar = {
  Root,
  TimerBar,
  Content,
  Footer,
}
